export * from './generatedClient';

import { Client, Implementation } from './generatedClient';

class HTTPError extends Error {
  status: number;
  response: Response;

  constructor(status: number, statusText: string, response: Response, message?: string) {
    super(message || `HTTPError ${status}: ${statusText}`);
    this.name = 'HTTPError';
    this.status = status;
    this.response = response;
  }
}

export const buildInfluenceClient = ({ url, token, getToken, backendServiceToken }: {
  url: string;
  token?: string | null;
  getToken?: () => Promise<string | null>;
  backendServiceToken?: string;
}) => {
  const customImplementation: Implementation = async (method, path, params) => {
    const endpoint = new URL(path, url).toString();

    const hasBody = !['get', 'delete'].includes(method);
    const hasParams = Object.keys(params).length > 0;
    const hasFile = Object.values(params).some(
      (value) =>
        value instanceof File ||
        value instanceof Blob ||
        (Array.isArray(value) &&
          value.some((item) => item instanceof File || item instanceof Blob))
    );

    if (hasFile && !hasBody) {
      throw new Error('Files cannot be sent without a body');
    }

    const searchParams =
      hasBody && hasParams ? '' : `?${new URLSearchParams(params as any)}`;

    const contentType = hasBody && !hasFile ? 'application/json' : undefined;
    const formData = new FormData();
    if (hasFile) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(key, item);
          });
        } else {
          formData.append(key, value as any);
        }
      });
    }

    const body = hasFile
      ? formData
      : hasBody
        ? JSON.stringify(params)
        : undefined;

    // Always get the current token from Clerk (uses internal cache)
    const finalToken = token ?? (getToken ? await getToken() : null);
    const authHeader = finalToken ? `Bearer ${finalToken}` : undefined;

    const fullUrl = new URL(searchParams, endpoint).toString();

    const response = await fetch(fullUrl, {
      method: method.toUpperCase(),
      headers: {
        ...(authHeader ? { Authorization: authHeader } : {}),
        ...(contentType ? { 'Content-Type': contentType } : {}),
        ...(backendServiceToken ? { 'service-authorization': backendServiceToken } : {}),
      },
      body,
    });

    return response.json();
  };

  return new Client(customImplementation);
};

export type InfluenceAPIClient = ReturnType<typeof buildInfluenceClient>;
